import React, { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, Typography, CircularProgress, TextField, IconButton, InputAdornment, Stack, Button, TableContainer, TableSortLabel, Pagination, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { fetchProformaInvoices, updateProformaInvoiceStatus } from '../../api';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

export default function ProformaInvoices() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDialogItems, setItemDialogItems] = useState([]);
  const [itemDialogInvoice, setItemDialogInvoice] = useState(null);

  const handleOpenItemsDialog = async (invoice) => {
    setItemDialogInvoice(invoice);
    setItemDialogOpen(true);
    const res = await fetch(`/api/proforma-invoices/${invoice.id}`);
    const data = await res.json();
    setItemDialogItems(data?.data?.items || []);
  };
  const handleCloseItemsDialog = () => {
    setItemDialogOpen(false);
    setItemDialogItems([]);
    setItemDialogInvoice(null);
  };

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('desc');
  const navigate = useNavigate();

  const [summary, setSummary] = useState({ totalCount: 0, totalAmount: 0, openCount: 0, openAmount: 0 });

  useEffect(() => {
    fetchProformaInvoices()
      .then((data) => {
        let rows = data.data || [];
        if (search) {
          rows = rows.filter((r) =>
            r.invoice_no?.toLowerCase().includes(search.toLowerCase()) ||
            r.party_name?.toLowerCase().includes(search.toLowerCase())
          );
        }
        rows = rows.sort((a, b) => {
          let aVal = a[orderBy], bVal = b[orderBy];
          if (['final_amount', 'item_count', 'id'].includes(orderBy)) { aVal = Number(aVal); bVal = Number(bVal); }
          else if (orderBy === 'invoice_no') {
            const numOf = (s) => Number(String(s).match(/(\d+)\s*$/)?.[1] || 0);
            const cmp = numOf(aVal) - numOf(bVal);
            if (cmp !== 0) return order === 'asc' ? cmp : -cmp;
          }
          if (aVal < bVal) return order === 'asc' ? -1 : 1;
          if (aVal > bVal) return order === 'asc' ? 1 : -1;
          return 0;
        });
        setInvoices(rows);
        let totalAmount = 0, openCount = 0, openAmount = 0;
        rows.forEach((r) => {
          totalAmount += Number(r.final_amount || 0);
          if ((r.status || 'Open') === 'Open') { openCount++; openAmount += Number(r.final_amount || 0); }
        });
        setSummary({ totalCount: rows.length, totalAmount, openCount, openAmount });
      })
      .finally(() => setLoading(false));
  }, [search, orderBy, order]);

  return (
    <Box p={1}>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <MuiAlert elevation={6} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Stack direction="row" spacing={1} mb={1}>
        {[{
          label: `Total Proforma Invoices (${summary.totalCount})`, value: `₹${summary.totalAmount.toLocaleString()}`, bgcolor: '#e3f2fd', color: '#1565c0'
        }, {
          label: `Open (${summary.openCount})`, value: `₹${summary.openAmount.toLocaleString()}`, bgcolor: '#fff3e0', color: '#ef6c00'
        }].map((card, idx) => (
          <Box key={idx} sx={{ px: 1, py: 0.5, bgcolor: card.bgcolor, borderRadius: 2, minWidth: 150, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${card.color}` }}>
            <Typography variant="caption" sx={{ color: card.color }}>{card.label}</Typography>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: card.color }}>{card.value}</Typography>
          </Box>
        ))}
        <Box flex={2} display="flex" justifyContent="flex-end" alignItems="center">
          <Button variant="contained" color="warning" size="small" startIcon={<AddIcon />} sx={{ minWidth: 120 }} onClick={() => navigate('/accounts/sale?type=pi')}>Create PI</Button>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} mb={1} alignItems="center">
        <TextField
          placeholder="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { height: 28 }
          }}
          sx={{ width: 420, height: 28, '& .MuiInputBase-root': { height: 28, minHeight: 28 } }}
        />
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? <CircularProgress size={24} sx={{ m: 1 }} /> : (
          <Table size="small" sx={{ fontSize: 12 }}>
            <TableHead sx={{ bgcolor: '#fafbfc' }}>
              <TableRow sx={{ height: 32 }}>
                <TableCell sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}><TableSortLabel active={orderBy === 'invoice_no'} direction={orderBy === 'invoice_no' ? order : 'asc'} onClick={() => { setOrderBy('invoice_no'); setOrder(orderBy === 'invoice_no' && order === 'asc' ? 'desc' : 'asc'); }} sx={{ fontSize: 13 }}>PI No.</TableSortLabel></TableCell>
                <TableCell sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}><TableSortLabel active={orderBy === 'party_name'} direction={orderBy === 'party_name' ? order : 'asc'} onClick={() => { setOrderBy('party_name'); setOrder(orderBy === 'party_name' && order === 'asc' ? 'desc' : 'asc'); }} sx={{ fontSize: 13 }}>Customer</TableSortLabel></TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}><TableSortLabel active={orderBy === 'item_count'} direction={orderBy === 'item_count' ? order : 'asc'} onClick={() => { setOrderBy('item_count'); setOrder(orderBy === 'item_count' && order === 'asc' ? 'desc' : 'asc'); }} sx={{ fontSize: 13 }}>Items</TableSortLabel></TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}>Taxable</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}>GST</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}>Extra Charge</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}>Extra Disc</TableCell>
                <TableCell align="right" sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}><TableSortLabel active={orderBy === 'final_amount'} direction={orderBy === 'final_amount' ? order : 'asc'} onClick={() => { setOrderBy('final_amount'); setOrder(orderBy === 'final_amount' && order === 'asc' ? 'desc' : 'asc'); }} sx={{ fontSize: 13 }}>Final Amnt</TableSortLabel></TableCell>
                <TableCell sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ py: 0.5, fontSize: 13, fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.slice((page - 1) * rowsPerPage, page * rowsPerPage).map((row) => (
                <TableRow key={row.id} sx={{ height: 32 }}>
                  <TableCell sx={{ py: 0.5, fontSize: 12, fontWeight: 700 }}>
                    {row.invoice_no}
                    <div style={{ fontSize: 9, fontWeight: 400, color: '#888', marginTop: 2 }}>
                      {(() => {
                        if (!row.proforma_date) return '-';
                        const d = new Date(row.proforma_date);
                        const pad = n => n < 10 ? '0' + n : n;
                        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return `${pad(d.getDate())}-${months[d.getMonth()]}-${d.getFullYear()}`;
                      })()}
                    </div>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: 12 }}><b>{row.party_name || '-'}</b></TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: 12 }}>
                    {row.item_count || 0}
                    <IconButton size="small" onClick={() => handleOpenItemsDialog(row)} title="View items">
                      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 13c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm0-10a4 4 0 100 8 4 4 0 000-8z"/></svg>
                    </IconButton>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: 12 }}>{row.taxable_amount ?? '-'}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: 11 }}>{row.gst_amount ?? '-'}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: 11, color: 'red', fontWeight: 700 }}>{row.other_charge ?? '-'}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: 11, color: 'green', fontWeight: 700 }}>{row.other_discount ?? '-'}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontSize: 12, fontWeight: 700, color: '#222' }}>₹ {Number(row.final_amount || 0).toLocaleString()}</TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: 12 }}>
                    <span
                      onClick={async () => {
                        const newStatus = (row.status || 'Open') === 'Open' ? 'Closed' : 'Open';
                        try {
                          await updateProformaInvoiceStatus(row.id, newStatus);
                          setInvoices(prev => prev.map(r => r.id === row.id ? { ...r, status: newStatus } : r));
                          setSnackbar({ open: true, message: `Marked as ${newStatus}`, severity: 'success' });
                        } catch { setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' }); }
                      }}
                      style={{
                        display: 'inline-block', width: 70, fontSize: 11, padding: '2px 6px', borderRadius: 4, textAlign: 'center', fontWeight: 600, cursor: 'pointer', userSelect: 'none',
                        color: (row.status || 'Open') === 'Open' ? '#f2994a' : '#219653',
                        border: `1.5px solid ${(row.status || 'Open') === 'Open' ? '#f2994a' : '#219653'}`,
                        background: (row.status || 'Open') === 'Open' ? '#fff3e0' : '#e8f5e9'
                      }}
                      title="Click to toggle status"
                    >{row.status || 'Open'}</span>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: 12 }}>
                    <IconButton size="small" onClick={() => navigate(`/accounts/sale?type=pi&id=${row.id}`)}><EditIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" mt={1}>
        <Pagination
          count={Math.ceil(invoices.length / rowsPerPage)}
          page={page}
          onChange={(_, val) => setPage(val)}
          size="small"
          color="primary"
        />
      </Box>

      <Dialog open={itemDialogOpen} onClose={handleCloseItemsDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 4 }}>
          <span>Items for PI <b>{itemDialogInvoice?.invoice_no}</b></span>
          <IconButton onClick={handleCloseItemsDialog} sx={{ color: '#fff', position: 'absolute', right: 8, top: 8 }}>
            <svg width="22" height="22" viewBox="0 0 24 24"><path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.89 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z"/></svg>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc', p: 2 }}>
          {itemDialogItems.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: 'center', color: '#888', py: 4 }}>No items found.</Typography>
          ) : (
            <Table size="small" sx={{ boxShadow: 1, borderRadius: 2, overflow: 'hidden' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>MRP</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Qty Unit</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>GST %</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Taxable Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Total Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemDialogItems.map((item, idx) => (
                  <TableRow key={item.id} sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#f4f7fa' }}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{item.item_name}</TableCell>
                    <TableCell>{item.mrp}</TableCell>
                    <TableCell>{item.qty} {item.unit}</TableCell>
                    <TableCell>{item.price}</TableCell>
                    <TableCell>{item.gst_percent}</TableCell>
                    <TableCell>{item.taxable_value}</TableCell>
                    <TableCell>{item.total_amt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseItemsDialog} color="primary" variant="contained" sx={{ minWidth: 100, fontWeight: 600, borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}